import React from "react";
import CompanyHeader from "./CompanyHeader";

export default function InvoiceTemplate({ data, layout }) {
  if (!data) return null;

  const {
    invoiceNumber,
    issueDate,
    company,
    customer,
    items,
    totals,
    qrCodeUrl,
    invoiceTitle,
  } = data;

  // Helper to replace variables in HTML
  const replaceVars = (html) => {
    if (!html) return "";
    let text = html;
    const replacements = {
      "{CompanyName}": company?.name || "",
      "{CompanyAddress}": company?.address || "",
      "{CompanyPhone}": company?.phone || "",
      "{CompanyEmail}": company?.email || "",
      "{CompanyTax}": company?.taxNumber || "",
      "{InvoiceNumber}": invoiceNumber,
      "{IssueDate}": new Date(issueDate).toLocaleDateString(),
      "{ReferenceNumber}": data.referenceNumber || "",
      "{CustomerName}": customer?.name || "",
      "{CustomerMobile}": customer?.mobile || "",
      "{CustomerAddress}": customer?.address || "",
      "{CustomerTax}": customer?.taxNumber || "",
      "{TotalBeforeTax}": totals?.subtotal?.toFixed(2) || "0.00",
      "{TotalTax}": totals?.totalVAT?.toFixed(2) || "0.00",
      "{TotalDiscount}": totals?.discountTotal?.toFixed(2) || "0.00",
      "{NetTotal}": totals?.net?.toFixed(2) || "0.00",
      "{User}": data.salesmanName || "",
    };

    Object.entries(replacements).forEach(([key, val]) => {
      text = text.replace(new RegExp(key, "g"), val);
    });
    
    return text;
  };

  // Helper to render table cells
  const renderCell = (col, item, idx) => {
    const qty = item.quantity ?? 0;
    const price = item.unitPrice ?? 0;
    const discount = item.discount ?? 0;
    const taxValue = item.taxValue ?? 0;
    const total = qty * price;
    const net = (total - discount) + taxValue;

    switch (col.key) {
      case "index": return idx + 1;
      case "item": return (
        <>
          <div style={{ fontWeight: "bold" }}>{item.arabicName || item.productName || "—"}</div>
          {item.description && <div style={{ fontSize: "0.8em", color: "#666" }}>{item.description}</div>}
        </>
      );
      case "unit": return item.unitName || "—";
      case "quantity": return qty;
      case "price": return price.toFixed(2);
      case "total": return total.toFixed(2);
      case "discount": return discount.toFixed(2);
      case "tax": return taxValue.toFixed(2);
      case "net": return net.toFixed(2);
      case "description": return item.description || "";
      default: return "";
    }
  };

  // Default columns configuration
  const defaultColumns = [
    { key: "item", labelAr: "الصنف", labelEn: "Item", width: "auto" },
    { key: "quantity", labelAr: "الكمية", labelEn: "Qty", width: "10%" },
    { key: "unit", labelAr: "الوحدة", labelEn: "Unit", width: "10%" },
    { key: "price", labelAr: "السعر", labelEn: "Price", width: "15%" },
    { key: "total", labelAr: "المجموع", labelEn: "Total", width: "15%" },
    { key: "discount", labelAr: "الخصم", labelEn: "Discount", width: "10%" },
    { key: "tax", labelAr: "الضريبة", labelEn: "Tax", width: "10%" },
    { key: "net", labelAr: "الصافي", labelEn: "Net", width: "15%" },
  ];

  let columns = defaultColumns;
  if (layout?.columns) {
    columns = layout.columns.filter(c => c.visible !== false).sort((a, b) => a.order - b.order);
  }

  const paperWidth = layout?.paperSize === "80mm" ? "80mm" : layout?.paperSize === "A5" ? "148mm" : "210mm";
  
  const containerStyle = {
    direction: "rtl",
    background: "#fff",
    width: paperWidth,
    minHeight: "297mm",
    margin: "auto",
    padding: layout?.paperSize === "80mm" ? "2mm" : "10mm",
    color: "#000",
    position: "relative",
    boxSizing: "border-box",
    fontFamily: "Tajawal, sans-serif",
    border: "1px solid #eee", // Added border for better visibility in preview
    ...((layout?.customCss) ? {} : {}) // Placeholder if we want to parse CSS object, but customCss is string
  };

  return (
    <>
      {layout?.customCss && (
        <style>
          {layout.customCss}
        </style>
      )}
      
      <div id="invoice-template-container" style={containerStyle}>
        
        {/* Custom Header HTML */}
        {layout?.headerHtml && (
          <div 
            className="custom-header" 
            dangerouslySetInnerHTML={{ __html: replaceVars(layout.headerHtml) }} 
            style={{ marginBottom: "20px" }}
          />
        )}

        {/* Default Header (if enabled) */}
        {(layout?.showCompanyHeader !== false) && (
          <CompanyHeader company={company} />
        )}

        <h3 style={{ textAlign: "center", borderTop: "2px solid #000", borderBottom: "2px solid #000", padding: "8px", margin: "20px 0", fontSize: "20px", fontWeight: "bold" }}>
          {invoiceTitle || "فاتورة ضريبية"}
        </h3>
        
        <table style={{ width: "100%", marginBottom: "20px", fontSize: "14px" }}>
          <tbody>
            <tr>
              <td style={{ textAlign: "right", width: "30%" }}>رقم الفاتورة:</td>
              <td style={{ textAlign: "center", width: "40%" }}>{invoiceNumber}</td>
              <td style={{ textAlign: "left", width: "30%" }}>Invoice No.</td>
            </tr>
            <tr>
              <td style={{ textAlign: "right" }}>تاريخ الفاتورة:</td>
              <td style={{ textAlign: "center" }}>
                {new Date(issueDate).toLocaleDateString()}
              </td>
              <td style={{ textAlign: "left" }}>Invoice Date</td>
            </tr>
            <tr>
              <td style={{ textAlign: "right" }}>اسم العميل:</td>
              <td style={{ textAlign: "center" }}>{customer?.name || "—"}</td>
              <td style={{ textAlign: "left" }}>Customer Name</td>
            </tr>
            <tr>
              <td style={{ textAlign: "right" }}>الرقم الضريبي:</td>
              <td style={{ textAlign: "center" }}>{customer?.taxNumber || "—"}</td>
              <td style={{ textAlign: "left" }}>VAT No.</td>
            </tr>
            <tr>
              <td style={{ textAlign: "right" }}>الجوال:</td>
              <td style={{ textAlign: "center" }}>{customer?.mobile || "—"}</td>
              <td style={{ textAlign: "left" }}>Mobile</td>
            </tr>
            <tr>
              <td style={{ textAlign: "right" }}>العنوان:</td>
              <td style={{ textAlign: "center" }}>{customer?.address || "—"}</td>
              <td style={{ textAlign: "left" }}>Address</td>
            </tr>
          </tbody>
        </table>

        {/* Items Table */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "25px", fontSize: "13px" }} border="1">
          <thead style={{ backgroundColor: "#f1f1f1" }}>
            <tr>
              {columns.map((col) => (
                <th key={col.key} style={{ padding: "8px", width: col.width, textAlign: "center" }}>
                  {col.labelAr}
                  <br />
                  <span style={{ fontSize: "0.8em", fontWeight: "normal" }}>{col.labelEn}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx}>
                {columns.map((col) => (
                  <td key={col.key} style={{ padding: "6px", textAlign: "center" }}>
                    {renderCell(col, item, idx)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals + QR */}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
          <table style={{ width: "65%", borderCollapse: "collapse", fontSize: "14px" }}>
            <tbody>
              <tr>
                <td style={{ textAlign: "right", padding: "6px" }}>الإجمالي</td>
                <td style={{ textAlign: "center", padding: "6px" }}>
                  {totals.subtotal.toFixed(2)} ﷼
                </td>
                <td style={{ textAlign: "left", padding: "6px" }}>Subtotal</td>
              </tr>
              <tr>
                <td style={{ textAlign: "right", padding: "6px" }}>الخصم</td>
                <td style={{ textAlign: "center", padding: "6px" }}>
                  {totals.discountTotal.toFixed(2)} ﷼
                </td>
                <td style={{ textAlign: "left", padding: "6px" }}>Discount</td>
              </tr>
              <tr>
                <td style={{ textAlign: "right", padding: "6px" }}>الضريبة</td>
                <td style={{ textAlign: "center", padding: "6px" }}>
                  {totals.totalVAT.toFixed(2)} ﷼
                </td>
                <td style={{ textAlign: "left", padding: "6px" }}>VAT</td>
              </tr>
              <tr style={{ fontWeight: "bold", backgroundColor: "#f9f9f9" }}>
                <td style={{ textAlign: "right", padding: "6px" }}>الصافي</td>
                <td style={{ textAlign: "center", padding: "6px" }}>
                  {totals.net.toFixed(2)} ﷼
                </td>
                <td style={{ textAlign: "left", padding: "6px" }}>Net Total</td>
              </tr>
            </tbody>
          </table>

          {qrCodeUrl && (
            <div style={{ textAlign: "center", width: "30%" }}>
              <img
                src={qrCodeUrl}
                alt="QR Code"
                style={{
                  width: "120px",
                  height: "120px",
                  border: "1px solid #ddd",
                  padding: "5px",
                  borderRadius: "5px",
                }}
                onError={(e) => (e.target.style.display = "none")}
              />
              <div style={{ marginTop: "5px", fontSize: "12px" }}>
                رمز الاستجابة السريع
              </div>
            </div>
          )}
        </div>

        {/* Custom Footer HTML */}
        {layout?.footerHtml && (
          <div 
            className="custom-footer" 
            dangerouslySetInnerHTML={{ __html: replaceVars(layout.footerHtml) }} 
            style={{ marginTop: "30px" }}
          />
        )}

        {/* Default Footer */}
        {(layout?.showCompanyHeader !== false && !layout?.footerHtml) && (
          <div style={{ position: "absolute", bottom: "10mm", left: 0, width: "100%", textAlign: "center", fontSize: "11px", color: "#777", borderTop: "1px solid #ddd", paddingTop: "4px" }}>
            {company?.address} | {company?.phone} | {company?.email}
          </div>
        )}
      </div>
    </>
  );
}